import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { getEmployee } from '../../api/employees';
import { Wrench, ArrowRightLeft } from 'lucide-react';

const statusClass = {
  active: 'badge-active',
  expired: 'badge-expired',
  migrated: 'badge-migrated',
};

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const AllocationHistoryModal = ({ isOpen, onClose, employee }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && employee?.id) {
      setLoading(true);
      getEmployee(employee.id)
        .then((res) => setData(res.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }
  }, [isOpen, employee]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`History — ${employee?.name || ''}`} size="xl">
      {loading ? (
        <div className="flex justify-center py-8">
          <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>
      ) : !data ? (
        <p className="text-center text-gray-400 py-8">No data found.</p>
      ) : (
        <div className="space-y-5">
          {/* Employee info summary */}
          <div className="bg-gray-50 rounded-xl p-4 flex flex-wrap gap-4 text-sm">
            <div><span className="text-gray-400">Email</span><p className="font-medium text-gray-800">{data.employee.email}</p></div>
            <div><span className="text-gray-400">Department</span><p className="font-medium text-gray-800">{data.employee.department}</p></div>
            <div><span className="text-gray-400">Joined</span><p className="font-medium text-gray-800">{fmt(data.employee.created_at)}</p></div>
          </div>

          {/* Allocation history */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4 text-primary-600" />
              <h4 className="text-sm font-semibold text-gray-700">Tool Allocations ({data.allocations.length})</h4>
            </div>
            {data.allocations.length === 0 ? (
              <p className="text-sm text-gray-400">No allocations found.</p>
            ) : (
              <div className="space-y-2">
                {data.allocations.map((a) => (
                  <div key={a.id} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{a.tool_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {fmt(a.start_date)} → {fmt(a.end_date)}
                        {a.tool_identifier && <span className="ml-2 text-gray-500">· ID: {a.tool_identifier}</span>}
                      </p>
                      {a.notes && <p className="text-xs text-gray-400 mt-0.5 italic">{a.notes}</p>}
                    </div>
                    <span className={statusClass[a.status] || 'badge-expired'}>{a.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Migration history */}
          {data.migrations.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ArrowRightLeft className="w-4 h-4 text-blue-500" />
                <h4 className="text-sm font-semibold text-gray-700">Migrations ({data.migrations.length})</h4>
              </div>
              <div className="space-y-2">
                {data.migrations.map((m) => (
                  <div key={m.id} className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">{m.from_tool_name || '—'}</span>
                        <span className="mx-2 text-blue-400">→</span>
                        <span className="font-medium text-blue-700">{m.to_tool_name}</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Migrated on {fmt(m.migration_date)}</p>
                      {m.notes && <p className="text-xs text-gray-400 italic mt-0.5">{m.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default AllocationHistoryModal;
